import React from 'react';
import * as THREE from 'three';
import { invalidate, ThreeEvent } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { cadToThreeJsPosition } from '../../Scenes/scene-utils';
import { buildMediaUrl, loadGeometryFromUrl, PlacedGeometry } from './geometryLoader';
import { STOPE_SHAPE_COLOURS, STOPE_SHAPE_OPACITY, MAX_STOPE_SHAPE_OPACITY } from './stopeShapeColours';
import { StopeShapeDateRange } from './stopeShapeDateRange';
import { getStopeFillAtDay, StopeFillState, StopePourData } from './stopePourTimeline';
import { getStopeLegendBinColour } from '../../Components/StopeLegendBuilder/stopeLegend';
import type { StopeLegendConfig, StopeLegendSource } from '../../Components/StopeLegendBuilder/stopeLegend';

interface StopeShapeOriginCoords {
  x: number;
  y: number;
  z: number;
}

export interface StopeShapeInfo extends StopeLegendSource {
  stopeId: string;
  stopeName?: string;
  geometryPath: string;
  originCoords?: StopeShapeOriginCoords;
  dateInterval?: StopeShapeDateRange | null;
}

interface StopeShapeRendererProps {
  stopes: StopeShapeInfo[];
  offset: THREE.Vector3;
  scale: number;
  timelineDay?: number | null;
  legendConfig?: StopeLegendConfig | null;
  opacityScale?: number;
  pourData?: StopePourData;
  stepDays?: number;
  hoveredStopeId?: string | null;
  onHoverChange?: (stopeId: string, isHovered: boolean) => void;
  onSelect?: (stopeId: string) => void;
}

const noopRaycast = () => {};
// Pointer travel (px) beyond which a click is treated as a camera drag, not a selection.
const CLICK_DRAG_THRESHOLD_PX = 5;
const EDGE_THRESHOLD_ANGLE = 30;
const EDGE_OPACITY_FLOOR = 0.35;
const HOVER_OPACITY_BOOST = 0.2;
const HOVER_EMISSIVE_INTENSITY = 0.35;
const CUSTOM_LEGEND_OPACITY = 0.48;
const STOPE_SHAPE_HOVER_COLOURS: Record<keyof typeof STOPE_SHAPE_COLOURS, string> = {
  completed: '#39ff6b',
  active: '#18c7ff',
  planned: '#ffb000',
  idle: '#cfd6e4',
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const scaleStatusOpacity = (base: number, opacityScale: number): number => (
  clamp01((base / MAX_STOPE_SHAPE_OPACITY) * opacityScale)
);

const getDisplayColour = (status: keyof typeof STOPE_SHAPE_COLOURS, isHovered: boolean): THREE.Color => (
  new THREE.Color(isHovered ? STOPE_SHAPE_HOVER_COLOURS[status] : STOPE_SHAPE_COLOURS[status])
);

const getDisplayOpacity = (opacity: number, isHovered: boolean): number => (
  isHovered ? clamp01(opacity + HOVER_OPACITY_BOOST) : opacity
);

const getHeadlineStatus = (fill: StopeFillState): keyof typeof STOPE_SHAPE_COLOURS => {
  if (fill.isActive) return 'active';
  if (fill.completedRatio >= 0.995) return 'completed';
  if (fill.completedRatio > 0) return 'active';
  if (fill.plannedRatio > 0) return 'planned';
  return 'idle';
};

const getGeometryVerticalBounds = (geometry: THREE.BufferGeometry) => {
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const minY = geometry.boundingBox?.min.y ?? 0;
  const maxY = geometry.boundingBox?.max.y ?? minY;

  return { minY, maxY };
};

const getGeometryAlignmentOffset = (
  geometry: THREE.BufferGeometry,
  geometryPosition: THREE.Vector3,
  originCoords: StopeShapeOriginCoords | undefined,
): THREE.Vector3 => {
  if (!originCoords) return new THREE.Vector3();

  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const { boundingBox } = geometry;
  if (!boundingBox) return new THREE.Vector3();

  const center = boundingBox.getCenter(new THREE.Vector3()).add(geometryPosition);
  const size = boundingBox.getSize(new THREE.Vector3());
  const origin = cadToThreeJsPosition(originCoords);
  const distanceFromOrigin = center.distanceTo(origin);
  const geometryRadius = Math.max(size.length() / 2, 1);
  const maxExpectedOffset = Math.max(geometryRadius * 12, 1000);

  return distanceFromOrigin > maxExpectedOffset
    ? origin.sub(center)
    : new THREE.Vector3();
};

const getGeometryLabelPosition = (geometry: THREE.BufferGeometry): THREE.Vector3 => {
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const { boundingBox } = geometry;
  if (!boundingBox) return new THREE.Vector3();

  const center = boundingBox.getCenter(new THREE.Vector3());
  const size = boundingBox.getSize(new THREE.Vector3());
  const labelOffset = Math.max(size.length() * 0.025, 5);

  return new THREE.Vector3(center.x, boundingBox.max.y + labelOffset, center.z);
};

const getStopeShapeLabel = (stope: StopeShapeInfo): string => (
  stope.stopeName?.trim() || stope.stopeId
);

const STOPE_FILL_VERTEX_SHADER = `
  varying float vLocalY;
  varying vec3 vNormal;

  void main() {
    vLocalY = position.y;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Three vertical bands: green up to completedY (actual fill), orange up to plannedY (planned-but-not-poured),
// grey above (not yet filled). The grey band is rendered fainter so empty stopes read as grey ghosts.
const STOPE_FILL_FRAGMENT_SHADER = `
  uniform vec3 completedColor;
  uniform vec3 plannedColor;
  uniform vec3 idleColor;
  uniform vec3 frontColor;
  uniform float completedY;
  uniform float plannedY;
  uniform float boundaryWidth;
  uniform float opacity;
  uniform float idleAlpha;
  varying float vLocalY;
  varying vec3 vNormal;

  void main() {
    float belowPlanned = step(vLocalY, plannedY);
    float belowCompleted = step(vLocalY, completedY);
    vec3 color = idleColor;
    color = mix(color, plannedColor, belowPlanned);
    color = mix(color, completedColor, belowCompleted);
    float alpha = opacity * mix(idleAlpha, 1.0, belowPlanned);
    float bCompleted = 1.0 - smoothstep(0.0, boundaryWidth, abs(vLocalY - completedY));
    float bPlanned = 1.0 - smoothstep(0.0, boundaryWidth, abs(vLocalY - plannedY));
    float boundary = max(bCompleted, bPlanned);
    color = mix(color, frontColor, boundary * 0.6);
    float light = 0.55 + 0.45 * max(dot(normalize(vNormal), normalize(vec3(0.35, 0.75, 0.55))), 0.0);
    gl_FragColor = vec4(color * light, alpha);
  }
`;

const ACTIVE_FRONT_COLOUR = '#18c7ff';
const NEUTRAL_FRONT_COLOUR = '#ffffff';
const IDLE_ALPHA_FACTOR = STOPE_SHAPE_OPACITY.idle / STOPE_SHAPE_OPACITY.completed;

interface StopeFillMaterialProps {
  geometry: THREE.BufferGeometry;
  completedRatio: number;
  plannedRatio: number;
  isActive: boolean;
  isHovered: boolean;
  opacityScale: number;
}

const StopeFillMaterial = ({
  geometry, completedRatio, plannedRatio, isActive, isHovered, opacityScale,
}: StopeFillMaterialProps) => {
  const material = React.useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      completedColor: { value: new THREE.Color(STOPE_SHAPE_COLOURS.completed) },
      plannedColor: { value: new THREE.Color(STOPE_SHAPE_COLOURS.planned) },
      idleColor: { value: new THREE.Color(STOPE_SHAPE_COLOURS.idle) },
      frontColor: { value: new THREE.Color(NEUTRAL_FRONT_COLOUR) },
      completedY: { value: 0 },
      plannedY: { value: 0 },
      boundaryWidth: { value: 0.1 },
      opacity: { value: STOPE_SHAPE_OPACITY.completed },
      idleAlpha: { value: IDLE_ALPHA_FACTOR },
    },
    vertexShader: STOPE_FILL_VERTEX_SHADER,
    fragmentShader: STOPE_FILL_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  }), []);

  React.useEffect(() => {
    const { minY, maxY } = getGeometryVerticalBounds(geometry);
    const height = Math.max(maxY - minY, 0.1);
    material.uniforms.completedY.value = minY + (height * completedRatio);
    material.uniforms.plannedY.value = minY + (height * plannedRatio);
    material.uniforms.boundaryWidth.value = height * 0.015;
    material.uniforms.frontColor.value = new THREE.Color(isActive ? ACTIVE_FRONT_COLOUR : NEUTRAL_FRONT_COLOUR);
    material.uniforms.opacity.value = getDisplayOpacity(scaleStatusOpacity(STOPE_SHAPE_OPACITY.completed, opacityScale), isHovered);
    material.needsUpdate = true;
    invalidate();
  }, [geometry, completedRatio, plannedRatio, isActive, isHovered, material, opacityScale]);

  React.useEffect(() => () => {
    material.dispose();
  }, [material]);

  return <primitive attach="material" object={material} />;
};

interface StopeShapeMeshProps {
  stope: StopeShapeInfo;
  geometry: THREE.BufferGeometry;
  geometryPosition: THREE.Vector3;
  fill: StopeFillState;
  legendConfig?: StopeLegendConfig | null;
  opacityScale: number;
  isHovered: boolean;
  onHoverChange?: (stopeId: string, isHovered: boolean) => void;
  onSelect?: (stopeId: string) => void;
}

const StopeShapeMesh = ({
  stope,
  geometry,
  geometryPosition,
  fill,
  legendConfig,
  opacityScale,
  isHovered,
  onHoverChange,
  onSelect,
}: StopeShapeMeshProps) => {
  const status = getHeadlineStatus(fill);
  const isInteractive = !!onHoverChange || !!onSelect;
  const legendColour = getStopeLegendBinColour(stope, legendConfig);
  const displayColour = React.useMemo(
    () => (legendColour ? new THREE.Color(legendColour) : getDisplayColour(status, isHovered)),
    [isHovered, legendColour, status],
  );
  const baseOpacity = scaleStatusOpacity(legendColour ? CUSTOM_LEGEND_OPACITY : STOPE_SHAPE_OPACITY[status], opacityScale);
  const displayOpacity = getDisplayOpacity(baseOpacity, isHovered);
  const edgesGeometry = React.useMemo(
    () => new THREE.EdgesGeometry(geometry, EDGE_THRESHOLD_ANGLE),
    [geometry],
  );
  const edgeColour = React.useMemo(() => displayColour.clone().multiplyScalar(0.45), [displayColour]);
  const edgeOpacity = clamp01(Math.max(displayOpacity, EDGE_OPACITY_FLOOR) * Math.min(1, opacityScale * 3));
  const meshPosition = React.useMemo(
    () => getGeometryAlignmentOffset(geometry, geometryPosition, stope.originCoords).add(geometryPosition),
    [geometry, geometryPosition, stope.originCoords],
  );
  const labelPosition = React.useMemo(
    () => getGeometryLabelPosition(geometry),
    [geometry],
  );

  React.useEffect(() => () => {
    edgesGeometry.dispose();
  }, [edgesGeometry]);

  React.useEffect(() => {
    if (!isHovered || !onSelect) return undefined;

    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = 'pointer';

    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [isHovered, onSelect]);

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    onHoverChange?.(stope.stopeId, true);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    onHoverChange?.(stope.stopeId, false);
  };

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    if (event.delta > CLICK_DRAG_THRESHOLD_PX) return;
    onSelect?.(stope.stopeId);
  };

  return (
    <group position={meshPosition}>
      <mesh
        geometry={geometry}
        raycast={isInteractive ? THREE.Mesh.prototype.raycast : noopRaycast}
        onPointerOver={isInteractive ? handlePointerOver : undefined}
        onPointerOut={isInteractive ? handlePointerOut : undefined}
        onClick={onSelect ? handleClick : undefined}
      >
        {legendColour ? (
          <meshPhongMaterial
            attach="material"
            color={displayColour}
            emissive={displayColour}
            emissiveIntensity={isHovered ? HOVER_EMISSIVE_INTENSITY : 0}
            transparent
            opacity={displayOpacity}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        ) : (
          <StopeFillMaterial
            geometry={geometry}
            completedRatio={fill.completedRatio}
            plannedRatio={fill.plannedRatio}
            isActive={fill.isActive}
            isHovered={isHovered}
            opacityScale={opacityScale}
          />
        )}
      </mesh>
      <lineSegments geometry={edgesGeometry} raycast={noopRaycast}>
        <lineBasicMaterial
          attach="material"
          color={edgeColour}
          transparent
          opacity={edgeOpacity}
          depthWrite={false}
        />
      </lineSegments>
      {isHovered && (
        <Billboard
          position={labelPosition}
          follow
          lockX={false}
          lockY={false}
          lockZ={false}
        >
          <Text
            fontSize={8}
            color="#ffcc00"
            anchorX="center"
            anchorY="middle"
          >
            {getStopeShapeLabel(stope)}
          </Text>
        </Billboard>
      )}
    </group>
  );
};

const StopeShapeRenderer = ({
  stopes,
  offset,
  scale,
  timelineDay,
  legendConfig,
  opacityScale = 1,
  pourData = {},
  stepDays = 1,
  hoveredStopeId,
  onHoverChange,
  onSelect,
}: StopeShapeRendererProps) => {
  const [geometries, setGeometries] = React.useState<Record<string, PlacedGeometry>>({});
  const geometriesRef = React.useRef<Record<string, PlacedGeometry>>({});
  geometriesRef.current = geometries;

  const pathsKey = React.useMemo(
    () => stopes.map((s) => s.geometryPath).sort().join('|'),
    [stopes],
  );

  const groupPosition = React.useMemo(
    () => offset.clone().multiplyScalar(scale),
    [offset, scale],
  );

  const visibleStopeIds = React.useMemo(() => new Set(stopes.map((s) => s.stopeId)), [stopes]);
  const stopeById = React.useMemo(() => new Map(stopes.map((s) => [s.stopeId, s])), [stopes]);
  const entries = Object.entries(geometries).filter(([stopeId]) => visibleStopeIds.has(stopeId));

  React.useEffect(() => {
    const toLoad = stopes.filter((s) => !geometriesRef.current[s.stopeId]);
    if (toLoad.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      toLoad.map(async (s) => {
        const placed = await loadGeometryFromUrl(buildMediaUrl(s.geometryPath));
        return { stopeId: s.stopeId, placed };
      }),
    ).then((results) => {
      if (cancelled) return;
      setGeometries((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          if (r.placed) {
            next[r.stopeId] = r.placed;
          }
        });
        return next;
      });
      invalidate();
    });

    return () => { cancelled = true; };
  }, [pathsKey]);

  React.useEffect(() => () => {
    Object.values(geometriesRef.current).forEach(({ geometry }) => geometry.dispose());
  }, []);

  if (entries.length === 0) return null;

  return (
    <group position={groupPosition} scale={scale}>
      {entries.map(([stopeId, { geometry, position }]) => {
        const stope = stopeById.get(stopeId);
        if (!stope) return null;

        const windowStart = timelineDay ?? null;
        const windowEnd = timelineDay != null ? timelineDay + stepDays : null;
        const fill = getStopeFillAtDay(pourData[stopeId], stope.volume, timelineDay, windowStart, windowEnd);

        return (
          <StopeShapeMesh
            key={stopeId}
            stope={stope}
            geometry={geometry}
            geometryPosition={position}
            fill={fill}
            legendConfig={legendConfig}
            opacityScale={opacityScale}
            isHovered={hoveredStopeId === stopeId}
            onHoverChange={onHoverChange}
            onSelect={onSelect}
          />
        );
      })}
    </group>
  );
};

export default StopeShapeRenderer;
